// AUTO-GENERATED by scripts/gen-routes.mjs - React Router v7 route table.
import type { ComponentType } from 'react';

import { lazy, Suspense } from 'react';
import { Outlet, createBrowserRouter } from 'react-router-dom';

import { SplashScreen } from 'src/components/loading-screen';
import { RouteErrorBoundary } from 'src/components/error-reporter';
import { RootLayout } from 'src/routes/root-layout';

import HomeLayout from 'src/app/(home)/layout';
import DashboardLayout from 'src/app/dashboard/layout';
import AccountLayout from 'src/app/dashboard/user/account/layout';
import Layout_about_us from 'src/app/about-us/layout';
import Layout_pricing from 'src/app/pricing/layout';
import Layout_faqs from 'src/app/faqs/layout';
import Layout_contact_us from 'src/app/contact-us/layout';
import Layout_coming_soon from 'src/app/coming-soon/layout';
import Layout_maintenance from 'src/app/maintenance/layout';
import Layout_payment from 'src/app/payment/layout';
import AuthLayout_sign_in from 'src/app/auth/firebase/sign-in/layout';
import AuthLayout_sign_up from 'src/app/auth/firebase/sign-up/layout';
import AuthLayout_reset_password from 'src/app/auth/firebase/reset-password/layout';
import AuthLayout_verify from 'src/app/auth/firebase/verify/layout';

// ----------------------------------------------------------------------

const toRoute = (m: { default: ComponentType }) => ({ Component: m.default });

const NotFound = lazy(() => import('src/app/not-found'));

// ----------------------------------------------------------------------

export const router = createBrowserRouter([
  {
    // App-wide providers live here so routing hooks have router context.
    element: <RootLayout />,
    children: [
      {
        // Pathless route: catches render crashes from any child and shows a
        // friendly fallback (still inside RootLayout, so theme/i18n are mounted)
        // while reporting to the make.com webhook via reportClientError.
        errorElement: <RouteErrorBoundary />,
        children: [
      {
        element: (
          <HomeLayout>
            <Outlet />
          </HomeLayout>
        ),
        children: [{ index: true, lazy: () => import('src/app/(home)/page').then(toRoute) }],
      },
  {
    element: (
      <Layout_about_us>
        <Outlet />
      </Layout_about_us>
    ),
    children: [{ path: 'about-us', lazy: () => import('src/app/about-us/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_pricing>
        <Outlet />
      </Layout_pricing>
    ),
    children: [{ path: 'pricing', lazy: () => import('src/app/pricing/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_faqs>
        <Outlet />
      </Layout_faqs>
    ),
    children: [{ path: 'faqs', lazy: () => import('src/app/faqs/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_contact_us>
        <Outlet />
      </Layout_contact_us>
    ),
    children: [{ path: 'contact-us', lazy: () => import('src/app/contact-us/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_coming_soon>
        <Outlet />
      </Layout_coming_soon>
    ),
    children: [{ path: 'coming-soon', lazy: () => import('src/app/coming-soon/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_maintenance>
        <Outlet />
      </Layout_maintenance>
    ),
    children: [{ path: 'maintenance', lazy: () => import('src/app/maintenance/page').then(toRoute) }],
  },
  {
    element: (
      <Layout_payment>
        <Outlet />
      </Layout_payment>
    ),
    children: [{ path: 'payment', lazy: () => import('src/app/payment/page').then(toRoute) }],
  },
      {
        path: 'auth/firebase',
        children: [
      { path: 'phone-sign-in', lazy: () => import('src/app/auth/firebase/phone-sign-in/page').then(toRoute) },
      {
        element: (
          <AuthLayout_reset_password>
            <Outlet />
          </AuthLayout_reset_password>
        ),
        children: [{ path: 'reset-password', lazy: () => import('src/app/auth/firebase/reset-password/page').then(toRoute) }],
      },
      {
        element: (
          <AuthLayout_sign_in>
            <Outlet />
          </AuthLayout_sign_in>
        ),
        children: [{ path: 'sign-in', lazy: () => import('src/app/auth/firebase/sign-in/page').then(toRoute) }],
      },
      {
        element: (
          <AuthLayout_sign_up>
            <Outlet />
          </AuthLayout_sign_up>
        ),
        children: [{ path: 'sign-up', lazy: () => import('src/app/auth/firebase/sign-up/page').then(toRoute) }],
      },
      {
        element: (
          <AuthLayout_verify>
            <Outlet />
          </AuthLayout_verify>
        ),
        children: [{ path: 'verify', lazy: () => import('src/app/auth/firebase/verify/page').then(toRoute) }],
      },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        ),
        children: [
      { path: 'audit-log', lazy: () => import('src/app/dashboard/audit-log/page').then(toRoute) },
      { path: 'beneficiaries/detail', lazy: () => import('src/app/dashboard/beneficiaries/detail/page').then(toRoute) },
      { path: 'chargebacks', lazy: () => import('src/app/dashboard/chargebacks/page').then(toRoute) },
      { path: 'collection', lazy: () => import('src/app/dashboard/collection/page').then(toRoute) },
      { path: 'commissions/dealers/detail', lazy: () => import('src/app/dashboard/commissions/dealers/detail/page').then(toRoute) },
      { path: 'commissions/payouts/detail', lazy: () => import('src/app/dashboard/commissions/payouts/detail/page').then(toRoute) },
      { path: 'commissions/payouts', lazy: () => import('src/app/dashboard/commissions/payouts/page').then(toRoute) },
      { path: 'commissions/promos', lazy: () => import('src/app/dashboard/commissions/promos/page').then(toRoute) },
      { path: 'commissions/tds', lazy: () => import('src/app/dashboard/commissions/tds/page').then(toRoute) },
      { path: 'commissions/tiers', lazy: () => import('src/app/dashboard/commissions/tiers/page').then(toRoute) },
      { path: 'credit', lazy: () => import('src/app/dashboard/credit/page').then(toRoute) },
      { path: 'credit/queue', lazy: () => import('src/app/dashboard/credit/queue/page').then(toRoute) },
      { path: 'credit/request', lazy: () => import('src/app/dashboard/credit/request/page').then(toRoute) },
      { path: 'dealers/bulk-invite', lazy: () => import('src/app/dashboard/dealers/bulk-invite/page').then(toRoute) },
      { path: 'dealers/detail', lazy: () => import('src/app/dashboard/dealers/detail/page').then(toRoute) },
      { path: 'dealers/edit', lazy: () => import('src/app/dashboard/dealers/edit/page').then(toRoute) },
      { path: 'dealers/grievance', lazy: () => import('src/app/dashboard/dealers/grievance/page').then(toRoute) },
      { path: 'dealers/new', lazy: () => import('src/app/dashboard/dealers/new/page').then(toRoute) },
      { path: 'dealers', lazy: () => import('src/app/dashboard/dealers/page').then(toRoute) },
      { path: 'deposit', lazy: () => import('src/app/dashboard/deposit/page').then(toRoute) },
      { path: 'disputes/detail', lazy: () => import('src/app/dashboard/disputes/detail/page').then(toRoute) },
      { path: 'disputes', lazy: () => import('src/app/dashboard/disputes/page').then(toRoute) },
      { path: 'distributors/edit', lazy: () => import('src/app/dashboard/distributors/edit/page').then(toRoute) },
      { path: 'distributors/new', lazy: () => import('src/app/dashboard/distributors/new/page').then(toRoute) },
      { path: 'distributors', lazy: () => import('src/app/dashboard/distributors/page').then(toRoute) },
      { path: 'earnings', lazy: () => import('src/app/dashboard/earnings/page').then(toRoute) },
      { path: 'error-log', lazy: () => import('src/app/dashboard/error-log/page').then(toRoute) },
      { path: 'eko-log', lazy: () => import('src/app/dashboard/eko-log/page').then(toRoute) },
      { path: 'exports', lazy: () => import('src/app/dashboard/exports/page').then(toRoute) },
      { path: 'float', lazy: () => import('src/app/dashboard/float/page').then(toRoute) },
      { path: 'geofence', lazy: () => import('src/app/dashboard/geofence/page').then(toRoute) },
      { path: 'grievances/detail', lazy: () => import('src/app/dashboard/grievances/detail/page').then(toRoute) },
      { path: 'grievances/new', lazy: () => import('src/app/dashboard/grievances/new/page').then(toRoute) },
      { path: 'grievances', lazy: () => import('src/app/dashboard/grievances/page').then(toRoute) },
      { path: 'history', lazy: () => import('src/app/dashboard/history/page').then(toRoute) },
      { path: 'kyc', lazy: () => import('src/app/dashboard/kyc/page').then(toRoute) },
      { path: 'kyc/resubmit', lazy: () => import('src/app/dashboard/kyc/resubmit/page').then(toRoute) },
      { path: 'ledger/entries', lazy: () => import('src/app/dashboard/ledger/entries/page').then(toRoute) },
      { path: 'ledger', lazy: () => import('src/app/dashboard/ledger/page').then(toRoute) },
      { path: 'manage-dealers/invite', lazy: () => import('src/app/dashboard/manage-dealers/invite/page').then(toRoute) },
      { path: 'manage-dealers', lazy: () => import('src/app/dashboard/manage-dealers/page').then(toRoute) },
      { path: 'network-activity', lazy: () => import('src/app/dashboard/network-activity/page').then(toRoute) },
      { path: 'notifications/templates', lazy: () => import('src/app/dashboard/notifications/templates/page').then(toRoute) },
      { path: 'notifications/test-send', lazy: () => import('src/app/dashboard/notifications/test-send/page').then(toRoute) },
      { path: 'ota', lazy: () => import('src/app/dashboard/ota/page').then(toRoute) },
      { index: true, lazy: () => import('src/app/dashboard/page').then(toRoute) },
      { path: 'privacy', lazy: () => import('src/app/dashboard/privacy/page').then(toRoute) },
      { path: 'profile', lazy: () => import('src/app/dashboard/profile/page').then(toRoute) },
      { path: 'provider-health', lazy: () => import('src/app/dashboard/provider-health/page').then(toRoute) },
      { path: 'reconciliation', lazy: () => import('src/app/dashboard/reconciliation/page').then(toRoute) },
      { path: 'reports/commission-paid', lazy: () => import('src/app/dashboard/reports/commission-paid/page').then(toRoute) },
      { path: 'reports/dispute-volume', lazy: () => import('src/app/dashboard/reports/dispute-volume/page').then(toRoute) },
      { path: 'reports/gmv', lazy: () => import('src/app/dashboard/reports/gmv/page').then(toRoute) },
      { path: 'reports/settlement', lazy: () => import('src/app/dashboard/reports/settlement/page').then(toRoute) },
      { path: 'reviewer/audit-log', lazy: () => import('src/app/dashboard/reviewer/audit-log/page').then(toRoute) },
      { path: 'reviewer/grievances/detail', lazy: () => import('src/app/dashboard/reviewer/grievances/detail/page').then(toRoute) },
      { path: 'reviewer/grievances', lazy: () => import('src/app/dashboard/reviewer/grievances/page').then(toRoute) },
      { path: 'reviewer/kyc/detail', lazy: () => import('src/app/dashboard/reviewer/kyc/detail/page').then(toRoute) },
      { path: 'reviewer/kyc', lazy: () => import('src/app/dashboard/reviewer/kyc/page').then(toRoute) },
      { path: 'reviewer/notifications', lazy: () => import('src/app/dashboard/reviewer/notifications/page').then(toRoute) },
      { path: 'reviewer', lazy: () => import('src/app/dashboard/reviewer/page').then(toRoute) },
      { path: 'reviewer/profile', lazy: () => import('src/app/dashboard/reviewer/profile/page').then(toRoute) },
      { path: 'send-money', lazy: () => import('src/app/dashboard/send-money/page').then(toRoute) },
      { path: 'senders', lazy: () => import('src/app/dashboard/senders/page').then(toRoute) },
      { path: 'settlement', lazy: () => import('src/app/dashboard/settlement/page').then(toRoute) },
      { path: 'settlement/weekly', lazy: () => import('src/app/dashboard/settlement/weekly/page').then(toRoute) },
      { path: 'transactions/approvals', lazy: () => import('src/app/dashboard/transactions/approvals/page').then(toRoute) },
      { path: 'transactions/detail', lazy: () => import('src/app/dashboard/transactions/detail/page').then(toRoute) },
      { path: 'transactions/failed', lazy: () => import('src/app/dashboard/transactions/failed/page').then(toRoute) },
      { path: 'transactions/stuck', lazy: () => import('src/app/dashboard/transactions/stuck/page').then(toRoute) },
      { path: 'users/detail', lazy: () => import('src/app/dashboard/users/detail/page').then(toRoute) },
      { path: 'users', lazy: () => import('src/app/dashboard/users/page').then(toRoute) },
      { path: 'variance', lazy: () => import('src/app/dashboard/variance/page').then(toRoute) },
          {
            path: 'user/account',
            element: (
              <AccountLayout>
                <Outlet />
              </AccountLayout>
            ),
            children: [
          { path: 'billing', lazy: () => import('src/app/dashboard/user/account/billing/page').then(toRoute) },
          { path: 'change-password', lazy: () => import('src/app/dashboard/user/account/change-password/page').then(toRoute) },
          { path: 'notifications', lazy: () => import('src/app/dashboard/user/account/notifications/page').then(toRoute) },
          { index: true, lazy: () => import('src/app/dashboard/user/account/page').then(toRoute) },
          { path: 'socials', lazy: () => import('src/app/dashboard/user/account/socials/page').then(toRoute) },
            ],
          },
        ],
      },
  { path: 'error/403', lazy: () => import('src/app/error/403/page').then(toRoute) },
  { path: 'error/404', lazy: () => import('src/app/error/404/page').then(toRoute) },
  { path: 'error/500', lazy: () => import('src/app/error/500/page').then(toRoute) },
      {
        path: '*',
        element: (
          <Suspense fallback={<SplashScreen />}>
            <NotFound />
          </Suspense>
        ),
      },
        ],
      },
    ],
  },
]);
